import { useEffect, useMemo, useState } from 'react'
import { useAssistantsByIds, useSearchAssistants } from '@/hooks/api/useAssistants'
import { InboxFilterOptions } from './InboxFilterOptions'

const SEARCH_DEBOUNCE_MS = 300

function useDebouncedValue(value: string, delayMs: number): string {
  const [debounced, setDebounced] = useState(value)
  useEffect(() => {
    const timeout = window.setTimeout(() => setDebounced(value), delayMs)
    return () => window.clearTimeout(timeout)
  }, [value, delayMs])
  return debounced
}

interface InboxAssistantOptionsProps {
  selected: number[]
  onChange: (selected: number[]) => void
  /** See `InboxFilterOptions`. Off in the mobile sheet so the keyboard stays closed. */
  autoFocus?: boolean
  /** See `InboxFilterOptions`. Off in the mobile sheet so the sheet is the only scroller. */
  scrollable?: boolean
  /** See `InboxFilterOptions`. */
  previewLimit?: number
}

/**
 * Assistant choices come from a server search. The preloaded assistant list is capped
 * and omits names once it fails to load, so filtering it locally hides real assistants.
 */
export function InboxAssistantOptions({
  selected,
  onChange,
  autoFocus,
  scrollable,
  previewLimit,
}: InboxAssistantOptionsProps) {
  const [query, setQuery] = useState('')
  const debouncedQuery = useDebouncedValue(query, SEARCH_DEBOUNCE_MS)
  const searchTerm = debouncedQuery.trim()
  const search = useSearchAssistants(searchTerm)
  const { data: selectedAssistants = [] } = useAssistantsByIds(selected)

  const isLoading = query.trim() !== searchTerm || search.isLoading || search.isPlaceholderData

  const options = useMemo(() => {
    const found = search.data ?? []
    const rows = searchTerm
      ? found
      : [
          ...selectedAssistants.filter(
            (assistant) => !found.some((item) => item.id === assistant.id)
          ),
          ...found,
        ]
    return rows.map((assistant) => ({ value: assistant.id, label: assistant.name }))
  }, [search.data, searchTerm, selectedAssistants])

  return (
    <InboxFilterOptions
      options={options}
      selected={selected}
      onChange={(ids) => onChange(ids.filter((id): id is number => typeof id === 'number'))}
      query={query}
      onQueryChange={setQuery}
      filterLocally={false}
      isLoading={isLoading}
      autoFocus={autoFocus}
      scrollable={scrollable}
      previewLimit={previewLimit}
    />
  )
}
